import React, { useEffect, useState } from 'react';
import { SnackbarProvider } from 'notistack';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  WelcomeScreen,
  MemberPreviewPage,
  ViewAssessment,
} from '@premkumarmph/assessment-library';
import {
  getAssessmentMemberRender,
  getAssessmentSubmissionResponse,
  saveAssessmentQuestionResponse,
  submitAssessment,
} from '../../api';
import { MAIN_CONTAINER } from '../../constants';
import useClickFooter from '../../../../hooks/useClickFooter';
import Loader from '../../../../components/atoms/Loader';
import SnackbarToast from '../../../../components/organisms/SnackbarToast';
import { SNACKBAR_TYPE } from '../../../../utils/constants';

const styles = {
  title: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '36px',
    color: '#101828',
    marginBottom: 16,
    textAlign: 'center',
  },
  orgTitle: {
    marginTop: 8,
    fontWeight: 600,
    lineHeight: '40px',
    fontFamily: 'Inter',
    color: '#101828',
    fontSize: 28,
    marginBottom: 55,
  },
  subHeader: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '20px',
    color: '#667085',
    textAlign: 'center',
  },
  startBtn: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 24,
    borderRadius: 40,
    padding: '10px 32px',
    backgroundColor: '#036fcb',
    height: 48,
  },
  button: { border: '1px solid #E4E7EC' },
  logo: { width: 255, height: 62, marginBottom: 0 },
};

// 'welcome' | 'filling' | 'viewing'
const AssessmentContainer = ({ onFooterItemClick, onSetNormalScreen, onSetFullScreen }) => {
  const navigate   = useNavigate();
  const location   = useLocation();
  useClickFooter(onFooterItemClick);

  const {
    assessmentUuid,
    assessmentSubmissionId,
    status: initialStatus,
    orgName,
    orgImageURL,
    assessmentName,
    patientId,
  } = location?.state || {};
console.log('AssessmentContainer received patientId: ', patientId);
  const statusUpper      = (initialStatus || '').toUpperCase();
  const isPendingOrDraft = statusUpper === 'PENDING' || statusUpper === 'DRAFT';
  const isCompleted      = statusUpper === 'COMPLETED' || statusUpper === 'EXPIRED';

  const [screenMode, setScreenMode] = useState(isPendingOrDraft ? 'welcome' : null);
  const [steps,      setSteps]      = useState([]);
  const [isLoading,  setIsLoading]  = useState(!isPendingOrDraft);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    onSetFullScreen();
    if (!isPendingOrDraft) {
      loadSubmissionResponse(isCompleted ? 'viewing' : 'filling');
    }
    return () => onSetNormalScreen();
  }, []); // eslint-disable-line

  const loadSubmissionResponse = async (mode = 'viewing') => {
    if (!assessmentSubmissionId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const result = await getAssessmentSubmissionResponse(assessmentSubmissionId);
      setSteps(result || []);
      setScreenMode(mode);
    } catch (err) {
      console.error('Error loading submission response', err);
      setSnackbarMessage('Failed to load assessment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartAssessment = async () => {
    console.log('Starting assessment with patientId: ', patientId, assessmentUuid);
    if (!assessmentUuid || !patientId) return;
    try {
      setIsLoading(true);
      const fetchedSteps = await getAssessmentMemberRender(assessmentUuid, patientId);
      setSteps(fetchedSteps || []);
      setScreenMode('filling');
    } catch (err) {
      console.error('Error loading assessment', err);
      setSnackbarMessage('Failed to load assessment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => navigate('/records/new/assessments_dashboard');

  const handleSubmitSuccess = () => navigate('/records/new/assessments_dashboard');

  if (isLoading) return <Loader text={MAIN_CONTAINER.LOADING_TEXT} />;

  if (screenMode === 'welcome') {
    return (
      <SnackbarProvider anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <WelcomeScreen
          title={assessmentName}
          logo={orgImageURL}
          // orgName={orgName}
          subheader={MAIN_CONTAINER.LANDING_PAGE_SUB_HEADING}
          handleStartAssessment={handleStartAssessment}
          handleExit={handleClose}
          styles={styles}
        />
      </SnackbarProvider>
    );
  }

  if (screenMode === 'filling') {
    return (
      <>
        <MemberPreviewPage
          fontSizeOffset={4}
          showSaveAndExit
          steps={steps}
          assessmentUuid={assessmentUuid}
          assessmentSubmissionId={assessmentSubmissionId}
          assessmentName={assessmentName}
          orgImageURL={orgImageURL}
          orgName={orgName}
          onExit={handleClose}
          onSubmitSuccess={handleSubmitSuccess}
          saveQuestionFn={saveAssessmentQuestionResponse}
          submitAssessmentFn={submitAssessment}
          showScore={false}
        />
        <SnackbarToast
          open={!!snackbarMessage}
          message={snackbarMessage ?? ''}
          duration={4000}
          onClose={() => setSnackbarMessage('')}
          type={SNACKBAR_TYPE.ERROR}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        />
      </>
    );
  }

  if (screenMode === 'viewing') {
    return (
      <ViewAssessment
        submissionSteps={steps}
        orgImageURL={orgImageURL}
        orgName={orgName}
        assessmentName={assessmentName}
        assessmentStatus={initialStatus}
        handleClose={handleClose}
        styles={{ logo: { width: '100%', maxHeight: '48px', maxWidth: '200px', marginBottom: '8px' } }}
      />
    );
  }

  return <Loader text={MAIN_CONTAINER.LOADING_TEXT} />;
};

export default AssessmentContainer;
